'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Ice Cream: Cartons, Cones, and Cold Plastic"
      author="MicroPlastics Editorial Team"
      date="May 5, 2026"
      readTime="7 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in ice cream from cartons, tubs, and cone wrappers"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Ice cream contains microplastics from three main sources: the
        <strong> polyethylene-lined paperboard carton</strong> (almost every
        supermarket pint and quart), <strong>plastic tubs</strong> for
        store-brand half-gallons, and <strong>plastic-coated cone sleeves</strong>.
        Cold doesn&apos;t protect against leaching, particles release through
        physical contact and abrasion during shipping, freezing-thaw cycles, and
        scooping. Glass-jarred artisan ice cream, hand-packed scoops in a paper
        cup, or homemade in glass is the lowest-microplastic option.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Every common ice-cream carton has an inner polyethylene (PE) plastic liner, paperboard alone would dissolve from the moisture.</>,
          <>Plastic tubs (HDPE #2 or PP #5) are even higher direct-contact exposure than PE-lined paperboard.</>,
          <>Cone sleeves are typically polypropylene-coated paper; the cone itself usually doesn&apos;t add plastic if it&apos;s a pure wafer or sugar cone.</>,
          <>Freezing does not stop leaching, particles release physically through abrasion and freeze-thaw stress cycles.</>,
          <>Hand-scooped in a paper cup or homemade in a glass container is the cleanest option.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why &ldquo;paperboard&rdquo; ice cream cartons aren&apos;t paper
      </h2>
      <p>
        The classic round pint or quart ice cream carton looks like waxed paper,
        but it&apos;s actually paperboard with a thin layer of polyethylene (LDPE
        or HDPE) bonded to the inside. Without the plastic liner, the paper would
        absorb moisture from the ice cream and disintegrate within hours. That
        liner sits in direct contact with the ice cream for the entire shelf life, typically 6–12 months.
      </p>
      <p>
        Plastic tubs (the rectangular half-gallon and gallon containers) are even
        more direct, typically HDPE #2 or polypropylene #5 with no paper at all.
        More plastic surface area means more particle transfer over time.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Does cold protect against leaching?
      </h2>
      <p>
        Heat is the biggest accelerator of plastic-to-food chemical leaching, so
        ice cream is better than hot food in this regard. But <em>physical</em>
        particle release happens through abrasion (scooping, scraping), freeze-thaw
        stress (each time the carton softens slightly during a counter pause), and
        the long contact time itself. Studies of frozen food contact materials
        consistently find low but measurable microplastic transfer.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Ice cream formats compared
      </h2>

      <BlogComparisonTable
        caption="Ice cream packaging by microplastic exposure"
        columns={[
          { key: 'format', label: 'Format' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { format: 'Hand-scooped in paper cup at a creamery', risk: 'Lowest', why: 'No long-term plastic storage contact' },
          { format: 'Glass-jarred artisan ice cream', risk: 'Low', why: 'Glass is inert; metal lid only' },
          { format: 'Homemade ice cream in glass container', risk: 'Lowest', why: 'Full ingredient and container control' },
          { format: 'Pint in PE-lined paperboard carton (Ben & Jerry\'s, Häagen-Dazs)', risk: 'Moderate', why: 'PE liner contact 6-12 months; freeze-thaw cycles' },
          { format: 'Plastic tub half-gallon (store brand)', risk: 'Higher', why: 'Larger plastic surface; HDPE/PP wall contact' },
          { format: 'Single-serve plastic cups (with plastic spoon)', risk: 'Higher', why: 'High surface-area-to-volume + plastic utensil' },
          { format: 'Ice cream sandwich (plastic-wrapped)', risk: 'Moderate', why: 'Plastic wrap contact with cookie surface' },
          { format: 'Soft-serve from plastic dispenser machine', risk: 'Moderate-high', why: 'Plastic internal tubing; hot-sanitized parts' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The plastic spoon issue
      </h2>
      <p>
        Single-serve ice cream cups often come with a small plastic spoon
        included. The spoon is typically polystyrene (#6) or polypropylene (#5),
        and it spends 10–20 minutes in direct contact with cold ice cream that you
        scrape, twist, and apply pressure to with your mouth. A wooden or
        stainless-steel spoon eliminates this source entirely.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Go to a creamery for a hand-scooped cone or paper-cup serving</strong>
          when you can. Best of all worlds.
        </li>
        <li>
          <strong>Choose glass-jarred ice cream</strong>. Jeni&apos;s, McConnell&apos;s,
          Salt &amp; Straw (in some markets), Van Leeuwen, and most farm-to-table
          brands offer glass options.
        </li>
        <li>
          <strong>Prefer pint cartons over plastic tubs</strong>, the
          paperboard-with-PE-liner is meaningfully less plastic surface than a
          full plastic tub.
        </li>
        <li>
          <strong>Transfer to a glass container at home</strong> if you buy a big
          tub, pre-portion into glass jars and refreeze.
        </li>
        <li>
          <strong>Use a wooden or stainless spoon</strong>, never the plastic
          spoon included with single-serves.
        </li>
        <li>
          <strong>Make ice cream at home</strong>, a simple no-churn recipe in a
          glass loaf pan needs just cream, condensed milk, and your flavour of
          choice.
        </li>
        <li>
          <strong>Skip flexible ice-pop sleeves</strong> (Otter Pops, etc.), the
          plastic film stays in contact with the ice for months.
        </li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-milk-and-dairy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in milk and dairy
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-plastic-containers" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in plastic containers
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does ice cream contain microplastics?',
            answer:
              'Yes. Ice cream picks up microplastics from polyethylene-lined paperboard cartons, plastic tubs, single-serve plastic cups, and plastic spoons. Freezing does not prevent particle release through abrasion and freeze-thaw cycles.',
          },
          {
            question: 'Are paperboard ice cream cartons safe?',
            answer:
              'Paperboard cartons are lined with low-density polyethylene (LDPE) to prevent the paper from disintegrating. The PE liner is in direct contact with the ice cream for the entire shelf life and releases low but measurable microplastic over time.',
          },
          {
            question: 'Is glass-jarred ice cream lower in microplastics?',
            answer:
              'Yes. Glass is chemically inert and does not leach microplastics or release particles. Brands like Jeni\'s, McConnell\'s, and Van Leeuwen offer glass-jarred options. Only the lid contains some plastic, with minimal direct contact.',
          },
          {
            question: 'Is plastic-tub ice cream worse than carton ice cream?',
            answer:
              'Generally yes. Plastic tubs (HDPE #2 or PP #5) have more plastic surface area in contact with ice cream than PE-lined paperboard cartons. Both leach, but tubs contribute more particles per gram of ice cream.',
          },
          {
            question: 'Are plastic spoons included with ice cream safe?',
            answer:
              'Single-serve ice cream spoons are typically polystyrene (#6) or polypropylene (#5). They contact cold ice cream and your mouth during eating. Replace with a wooden or stainless-steel spoon to eliminate this source.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the release of microplastics and nanoplastics from plastic containers and reusable food pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2016.4501',
          },
          {
            authors: 'Da Costa Filho PA, Andrey D, Eriksen B, et al.',
            year: 2021,
            title: 'Detection and characterization of small-sized microplastics in milk products',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/s41598-021-04489-w',
          },
          {
            authors: 'IARC Working Group',
            year: 2019,
            title: 'Styrene, Styrene-7,8-oxide, and Quinoline (IARC Monograph 121)',
            publisher: 'International Agency for Research on Cancer',
            url: 'https://publications.iarc.fr/581',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
